import {Component, ElementRef, inject, Input, NgZone, OnDestroy, OnInit, Renderer2} from '@angular/core';
import {Block, BlockType, Column, Group, PdfPart, Row} from '@paperlessio/sdk/api/models';
import {BlockSerializer} from '@paperlessio/sdk/api/serializers';
import {DraggableService} from '../draggable.service';
import {DropZone} from './drop-zone';
import {Platform} from '@angular/cdk/platform';
import {AbsoluteDragService} from '@shared/absolute-drag/absolute-drag.service';

@Component({
  selector: 'app-drop-zone',
  templateUrl: './drop-zone.component.html',
  styleUrls: [
    './drop-zone.component.scss',
    './drop-zone.component.page.scss',
    './drop-zone.component.column.scss'
  ]
})
export class DropZoneComponent implements OnInit, OnDestroy {
  @Input() parent: Block;
  @Input() block?: Block;
  @Input() children?: Block[];
  @Input() disabled = false;
  @Input() scaleFactor = 1;

  private unlistenFunctions: any[] = [];

  private isMac = /(Mac|iPhone|iPod|iPad)/i.test(navigator.platform);

  get groupStyles() {
    return this.block instanceof Group ? this.block.styles : null;
  }

  get isRow() {
    return this.block instanceof Row;
  }

  get isColumn() {
    return this.block instanceof Column;
  }

  get isColumnChild() {
    return this.parent instanceof Column;
  }

  get isOverlay() {
    return this.parent instanceof PdfPart;
  }

  elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  draggableService = inject(DraggableService, {optional: true});

  private absoluteDragService = inject(AbsoluteDragService, {optional: true});
  private renderer = inject(Renderer2);
  private zone = inject(NgZone);
  private platform = inject(Platform);

  private counter = 0;

  ngOnInit() {
    if (this.disabled) {
      return;
    }

    this.zone.runOutsideAngular(() => {
      const element = this.elementRef.nativeElement;

      this.unlistenFunctions.push(this.renderer.listen(element, 'dragenter', (event: DragEvent) => {
        if (!this.draggableService.isDragging$.value) {
          return;
        }

        // When a partial is being dragged, the block will be null.
        // Also, partials can't have dropZone restrictions, so we don't need to worry.
        if (this.draggableService.block != null && this.draggableService.block.dropOnPageOnly && this.parent.type !== BlockType.Page) {
          return;
        }

        if (this.platform.SAFARI) {
          this.counter += 1;
        }

        const disableTop = (this.draggableService.block?.disableDropping?.top || this.block?.disableDropzone?.top) || false;
        const disableRight = (this.draggableService.block?.disableDropping?.right || this.block?.disableDropzone?.right) || false;
        const disableBottom = (this.draggableService.block?.disableDropping?.bottom || this.block?.disableDropzone?.bottom) || false;
        const disableLeft = (this.draggableService.block?.disableDropping?.left || this.block?.disableDropzone?.left) || false;

        const disableClasses = [
          disableTop ? 'disable-top' : null,
          disableRight ? 'disable-right' : null,
          disableBottom ? 'disable-bottom' : null,
          disableLeft ? 'disable-left' : null
        ].filter(v => !!v);

        if (Array.from(element.children).includes(event.target as HTMLElement)) {
          element.classList.add(
            'dragged',
            ...disableClasses
          );
        }

        (event.target as HTMLElement).classList.add(
          'dragged',
          ...disableClasses
        );

        if (this.isOverlay) {
          this.absoluteDragService.selectDropZone(this);
        }
      }));

      this.unlistenFunctions.push(this.renderer.listen(element, 'dragleave', (event: DragEvent) => {
        if (!this.draggableService.isDragging$.value) {
          return;
        }

        if (this.platform.SAFARI) {
          this.counter -= 1;
        }

        if (this.counter <= 0 || !this.platform.SAFARI) {
          if (!Array.from(element.children).includes(event.relatedTarget as Element)) {
            element.classList.remove(
              'dragged',
              'disable-top',
              'disable-right',
              'disable-bottom',
              'disable-left'
            );
          }
        }

        (event.target as HTMLElement).classList.remove(
          'dragged',
          'disable-top',
          'disable-right',
          'disable-bottom',
          'disable-left'
        );
      }));
    });
  }

  ngOnDestroy() {
    this.unlistenFunctions.forEach(unlistenFunction => {
      if (unlistenFunction && typeof unlistenFunction === 'function') {
        unlistenFunction();
      }
    });
  }

  drop(event: DragEvent) {
    // When a partial is being dragged, the block will be null.
    // Also, partials can't have dropZone restrictions, so we don't need to worry.
    if (this.draggableService?.block != null && this.draggableService.block.dropOnPageOnly && this.parent.type !== BlockType.Page) {
      return;
    }

    const target: HTMLElement = event.target as HTMLElement;
    const duplicate = this.isMac ? event.altKey : event.ctrlKey;
    const draggableService = this.draggableService;

    draggableService.relatedBlock = this.parent;
    draggableService.position = this.block?.position;
    draggableService.duplicate = duplicate;

    const disableTop = (this.draggableService.block?.disableDropping?.top || this.block?.disableDropzone?.top) || false;
    const disableRight = (this.draggableService.block?.disableDropping?.right || this.block?.disableDropzone?.right) || false;
    const disableBottom = (this.draggableService.block?.disableDropping?.bottom || this.block?.disableDropzone?.bottom) || false;
    const disableLeft = (this.draggableService.block?.disableDropping?.left || this.block?.disableDropzone?.left) || false;

    if (target.matches('.above') && !disableTop) {
      draggableService.zone = DropZone.Above;
    } else if (target.matches('.after') && !disableRight) {
      draggableService.relatedBlock = this.block;
      draggableService.zone = DropZone.After;
    } else if (target.matches('.below') && !disableBottom) {
      draggableService.zone = DropZone.Below;
      draggableService.position = this.block.position + 1;
    } else if (target.matches('.before') && !disableLeft) {
      draggableService.relatedBlock = this.block;
      draggableService.zone = DropZone.Before;
    } else if (target.matches('.inside')) {
      draggableService.zone = DropZone.Inside;
      draggableService.position = (this.children?.slice()?.pop()?.position ?? -1) + 1; // first child has position 0
    }

    if (this.isOverlay) {
      this.absoluteDragService.drop.next(event);
    }

    this.elementRef.nativeElement.classList.remove('dragged');
    target.classList.remove('dragged');

    // Handle Paperless block data from other buckets
    // Allows to duplicate blocks from other buckets by dragging them into the current bucket
    const items = event.dataTransfer?.items;
    if (items && items.length) {
      const item = Array.from(items).find(item => item.type === 'application/vnd.paperless+json');
      if (item) {
        item.getAsString((jsonString: string) => {
          const data = JSON.parse(jsonString);
          const block = new BlockSerializer().fromJson(data);
          if (!!block.bucket_type && !!block.bucket_id && (block.bucket_type !== this.parent.bucket_type || block.bucket_id !== this.parent.bucket_id)) {
            this.draggableService.duplicate = true;
            this.draggableService.block = block;
            this.draggableService.apply();
          }
        });
      }
    }

    return false;
  }
}
